import { c as _c } from "react/compiler-runtime";
import { createContext, PropsWithChildren, useContext } from 'react';
import useSWR from 'swr';
import { OperatorConfig, OperatorConfigUpdateInput, OutpostStatusResponse } from '../../../../../../typings/OutpostDeployment.interface';
import APIMethodKeys from '../../../client/APIMethodKeys';
import Icon from '../../common/base/Icon';
import { useToasts } from '../../common/Toast';
import { GlobalContext } from '../../contexts/GlobalContext';
import FullscreenLayout from '../../layouts/FullscreenLayout';
const STATUS_POLL_INTERVAL = 5000;
type Context = {
  config: OperatorConfig;
  updateConfig: (new_config: OperatorConfigUpdateInput) => Promise<OperatorConfig>;
  refreshConfig: () => Promise<OperatorConfig | undefined>;
  status: OutpostStatusResponse | null;
  refreshStatus: () => void;
};
export const OutpostContext = createContext<Context>({
  config: {} as OperatorConfig,
  updateConfig: () => Promise.resolve({} as OperatorConfig),
  refreshConfig: () => Promise.resolve(undefined),
  status: null,
  refreshStatus: () => Promise.resolve()
});
export const OutpostContextProvider = t0 => {
  const $ = _c(23);
  const {
    children
  } = t0;
  const {
    HookdeckAPI
  } = useContext(GlobalContext);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = APIMethodKeys.outpost.getStatus();
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== HookdeckAPI.outpost) {
    t2 = () => HookdeckAPI.outpost.getStatus();
    $[1] = HookdeckAPI.outpost;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      refreshInterval(latest) {
        return latest?.status === "UPDATING" || latest?.status === "INITIALIZING" ? STATUS_POLL_INTERVAL : 0;
      },
      revalidateOnFocus: true
    };
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const {
    data: status,
    error: status_error,
    mutate: mutateStatus,
    isLoading: is_status_loading
  } = useSWR(t1, t2, t3);
  const is_initializing = status?.status === "INITIALIZING";
  let t4;
  if ($[4] !== is_initializing) {
    t4 = !is_initializing ? APIMethodKeys.outpost.getConfig() : null;
    $[4] = is_initializing;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== HookdeckAPI.outpost) {
    t5 = () => HookdeckAPI.outpost.getConfig();
    $[6] = HookdeckAPI.outpost;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  const {
    data: config,
    error: config_error,
    mutate: mutate_config,
    isLoading: is_config_loading
  } = useSWR(t4, t5);
  let t6;
  if ($[8] !== HookdeckAPI.outpost || $[9] !== mutate_config) {
    t6 = async new_config => {
      const normalized = Object.fromEntries(Object.entries(new_config).filter(_temp).map(_temp2)) as OperatorConfigUpdateInput;
      const updated_config = await HookdeckAPI.outpost.updateConfig(normalized);
      mutate_config(updated_config);
      return updated_config;
    };
    $[8] = HookdeckAPI.outpost;
    $[9] = mutate_config;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  const updateConfig = t6;
  const is_loading = is_status_loading && !status_error || is_config_loading && !config_error;
  if (is_loading) {
    let t7;
    if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
      t7 = <FullscreenLayout center={true} no_decorations={true}><Icon icon="loading" muted={true} /></FullscreenLayout>;
      $[11] = t7;
    } else {
      t7 = $[11];
    }
    return t7;
  }
  let t7;
  if ($[12] !== config) {
    t7 = config ?? {} as OperatorConfig;
    $[12] = config;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  const t8 = status ?? null;
  let t9;
  if ($[14] !== mutateStatus || $[15] !== mutate_config || $[16] !== t7 || $[17] !== t8 || $[18] !== updateConfig) {
    t9 = {
      config: t7,
      updateConfig,
      refreshConfig: mutate_config,
      status: t8,
      refreshStatus: mutateStatus
    };
    $[14] = mutateStatus;
    $[15] = mutate_config;
    $[16] = t7;
    $[17] = t8;
    $[18] = updateConfig;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  let t10;
  if ($[20] !== children || $[21] !== t9) {
    t10 = <OutpostContext.Provider value={t9}>{children}</OutpostContext.Provider>;
    $[20] = children;
    $[21] = t9;
    $[22] = t10;
  } else {
    t10 = $[22];
  }
  return t10;
};
export const useOutpostConfig = () => {
  const $ = _c(3);
  const {
    config,
    updateConfig
  } = useContext(OutpostContext);
  let t0;
  if ($[0] !== config || $[1] !== updateConfig) {
    t0 = {
      config,
      updateConfig
    };
    $[0] = config;
    $[1] = updateConfig;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  return t0;
};

// Config API validation errors (422) carry one message per key in `data`,
// e.g. "RETRY_SCHEDULE must be a comma-separated list of integers with no
// spaces". Toasts take one string, so join them.
type FieldLabels = Record<string, string>;

// Messages start with the config key ("MAX_RETRY_LIMIT must be ..."); a form
// can map keys to the labels it shows so the toast reads "Max retries must be ...".
const labelKey = (message: string, labels: FieldLabels): string => {
  const [key, ...rest] = message.split(' ');
  return labels[key] ? [labels[key], ...rest].join(' ') : message;
};
const getConfigErrorMessage = (error: unknown, fallback: string, labels: FieldLabels): string => {
  if (!error || typeof error !== 'object' || !('response' in error)) {
    return fallback;
  }
  const response = error.response;
  if (!response || typeof response !== 'object') {
    return fallback;
  }
  const data = 'data' in response ? response.data : undefined;
  if (Array.isArray(data) && data.length > 0 && data.every(item => typeof item === 'string')) {
    return `${fallback}: ${data.map(item => labelKey(item, labels)).join('. ')}`;
  }
  const message = 'message' in response ? response.message : undefined;
  if (typeof message === 'string' && message && message !== 'Unprocessable Entity') {
    return `${fallback}: ${message}`;
  }
  return fallback;
};
type UpdateMessages = {
  success: string;
  failure: string;
  labels?: FieldLabels;
};

// updateConfig plus the boilerplate every settings form repeats: a success
// toast, a status refresh (the deployment redeploys), and a failure toast that
// carries the API's validation messages. Resolves null on failure so callers
// can `if (!result) return;` instead of try/catch.
export const useOutpostConfigUpdate = () => {
  const $ = _c(7);
  const {
    config,
    updateConfig,
    refreshStatus
  } = useContext(OutpostContext);
  const {
    addToast
  } = useToasts();
  let t0;
  if ($[0] !== addToast || $[1] !== refreshStatus || $[2] !== updateConfig) {
    t0 = async (input, messages) => {
      ;
      try {
        const result = await updateConfig(input);
        addToast("success", messages.success);
        await refreshStatus();
        return result;
      } catch (t1) {
        const error = t1;
        addToast("error", getConfigErrorMessage(error, messages.failure, messages.labels ?? {}));
        return null;
      }
    };
    $[0] = addToast;
    $[1] = refreshStatus;
    $[2] = updateConfig;
    $[3] = t0;
  } else {
    t0 = $[3];
  }
  const update = t0;
  let t1;
  if ($[4] !== config || $[5] !== update) {
    t1 = {
      config,
      updateConfig: update
    };
    $[4] = config;
    $[5] = update;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  return t1;
};
function _temp(t0) {
  const [, value] = t0;
  return value !== undefined;
}
function _temp2(t0) {
  const [key, value_0] = t0;
  return [key, value_0 === null ? null : String(value_0)];
}